import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  deletePortalCredential,
  importPortalCredentials,
  listPortalCredentials,
  revealPortalCredentialPassword,
  savePortalCredential
} from "../services/portalCredentialService";
import TECHNOPARKS from "../data/technoparks";

const emptyForm = {
  id: "",
  technoparkName: "",
  portalUrl: "",
  username: "",
  password: "",
  description: ""
};
const CUSTOM_TECHNOPARK_VALUE = "__other__";

const normalizeImportKey = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]/g, "");

const importAliases = {
  technoparkName: [
    "teknopark adı",
    "teknopark adi",
    "teknopark",
    "tekno park",
    "tekno park adı",
    "tekno park adi",
    "teknopark ismi",
    "tekno park ismi",
    "technopark",
    "technopark name"
  ],
  portalUrl: [
    "portal link",
    "portal linki",
    "portal url",
    "portal adresi",
    "portal",
    "link",
    "url",
    "teknopark url",
    "teknopark link",
    "teknopark linki",
    "teknopark adresi",
    "web sitesi",
    "website"
  ],
  username: [
    "kullanıcı adı",
    "kullanici adi",
    "kullanıcıadı",
    "kullaniciadi",
    "kullanıcı",
    "kullanici",
    "kullanıcı ismi",
    "kullanici ismi",
    "user",
    "username",
    "user name",
    "login",
    "e-posta",
    "eposta",
    "email",
    "mail"
  ],
  password: [
    "şifre",
    "sifre",
    "parola",
    "password",
    "pass"
  ],
  description: [
    "açıklama",
    "aciklama",
    "not",
    "notlar",
    "açıklamalar",
    "aciklamalar",
    "description",
    "description notes"
  ]
};

const getColumnIndex = (headerRow, names) => {
  const normalizedNames = names.map(normalizeImportKey);
  return headerRow.findIndex((cell) => normalizedNames.includes(normalizeImportKey(cell)));
};

const cellToText = (value) => String(value ?? "").trim();

const getPortalErrorMessage = (error) => {
  const code = String(error?.code || "");

  if (code === "functions/internal" || error?.message === "internal") {
    return "Portal şifreleme servisi henüz etkin değil. Sistem yöneticisinin Argevio sunucu fonksiyonunu devreye alması gerekiyor.";
  }

  return error?.message || "Bilinmeyen bir hata oluştu.";
};

const getRowsFromWorksheet = (worksheet, sheetName) => {
  const sheetRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
  const headerRowIndex = sheetRows.findIndex((row) => {
    const indexes = {
      technoparkName: getColumnIndex(row, importAliases.technoparkName),
      portalUrl: getColumnIndex(row, importAliases.portalUrl),
      username: getColumnIndex(row, importAliases.username),
      password: getColumnIndex(row, importAliases.password)
    };

    return Object.values(indexes).every((index) => index >= 0);
  });

  if (headerRowIndex < 0) return [];

  const headerRow = sheetRows[headerRowIndex];
  const indexes = {
    technoparkName: getColumnIndex(headerRow, importAliases.technoparkName),
    portalUrl: getColumnIndex(headerRow, importAliases.portalUrl),
    username: getColumnIndex(headerRow, importAliases.username),
    password: getColumnIndex(headerRow, importAliases.password),
    description: getColumnIndex(headerRow, importAliases.description)
  };

  return sheetRows
    .slice(headerRowIndex + 1)
    .map((row, rowIndex) => ({
      sourceLabel: `${sheetName} / Satır ${headerRowIndex + rowIndex + 2}`,
      technoparkName: cellToText(row[indexes.technoparkName]),
      portalUrl: cellToText(row[indexes.portalUrl]),
      username: cellToText(row[indexes.username]),
      password: cellToText(row[indexes.password]),
      description: indexes.description >= 0 ? cellToText(row[indexes.description]) : ""
    }))
    .filter((row) =>
      row.technoparkName || row.portalUrl || row.username || row.password || row.description
    );
};

function PortalCredentialsView({ credentialScope, notifyTeam, actorName = "Bir kullanıcı" }) {
  const scopeKind = credentialScope?.kind || "";
  const scopeTenantId = credentialScope?.tenantId || "";
  const resolvedCredentialScope = useMemo(
    () => ({ kind: scopeKind, ...(scopeTenantId ? { tenantId: scopeTenantId } : {}) }),
    [scopeKind, scopeTenantId]
  );
  const [credentials, setCredentials] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [isCustomTechnopark, setIsCustomTechnopark] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [copyMessage, setCopyMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadCredentials = useCallback(async () => {
    setLoading(true);
    try {
      setCredentials(await listPortalCredentials(resolvedCredentialScope));
    } catch (error) {
      console.error(error);
      alert("Portal bilgileri yüklenemedi: " + getPortalErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [resolvedCredentialScope]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const visibleCredentials = useMemo(() => {
    const search = searchText.trim().toLocaleLowerCase("tr");
    if (!search) return credentials;

    return credentials.filter((item) =>
      [item.technoparkName, item.portalUrl, item.username, item.description]
        .some((value) => String(value || "").toLocaleLowerCase("tr").includes(search))
    );
  }, [credentials, searchText]);

  const saveCredential = async () => {
    if (!form.technoparkName || !form.portalUrl || !form.username) {
      alert("Teknopark adı, portal linki ve kullanıcı adı zorunludur.");
      return;
    }

    if (!form.id && !form.password) {
      alert("Yeni portal kaydı için şifre zorunludur.");
      return;
    }

    setSaving(true);
    try {
      const wasEditing = Boolean(form.id);
      await savePortalCredential(resolvedCredentialScope, form);
      await notifyTeam?.({
        title: wasEditing ? "Portal bilgisi güncellendi" : "Yeni portal bilgisi eklendi",
        body: `${actorName}, ${form.technoparkName} portal bilgisini ${wasEditing ? "güncelledi" : "ekledi"}.`,
        type: "portal",
        link: "portalCredentials"
      });
      setForm(emptyForm);
      setIsCustomTechnopark(false);
      setIsFormOpen(false);
      await loadCredentials();
      alert(wasEditing ? "Portal bilgisi güncellendi." : "Portal bilgisi kaydedildi.");
    } catch (error) {
      console.error("Portal bilgisi kaydedilemedi", error);
      alert("Portal bilgisi kaydedilemedi: " + getPortalErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openNewCredentialModal = () => {
    setForm(emptyForm);
    setIsCustomTechnopark(false);
    setIsFormOpen(true);
  };

  const openEditCredentialModal = (item) => {
    setForm({ ...item, password: "" });
    setIsCustomTechnopark(!TECHNOPARKS.includes(item.technoparkName));
    setIsFormOpen(true);
  };

  const closeCredentialModal = () => {
    if (saving) return;
    setForm(emptyForm);
    setIsCustomTechnopark(false);
    setIsFormOpen(false);
  };

  const revealPassword = async (item) => {
    if (revealedPasswords[item.id]) {
      setRevealedPasswords((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      return;
    }

    try {
      const password = await revealPortalCredentialPassword(resolvedCredentialScope, item.id);
      setRevealedPasswords((current) => ({ ...current, [item.id]: password }));
    } catch (error) {
      alert("Şifre görüntülenemedi: " + getPortalErrorMessage(error));
    }
  };

  const writeToClipboard = async (value) => {
    if (!value) {
      throw new Error("Kopyalanacak değer bulunamadı.");
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const showCopyMessage = (message) => {
    setCopyMessage(message);
    window.setTimeout(() => setCopyMessage(""), 1800);
  };

  const copyUsername = async (item) => {
    try {
      await writeToClipboard(item.username);
      showCopyMessage("Kullanıcı adı kopyalandı.");
    } catch (error) {
      alert("Kullanıcı adı kopyalanamadı: " + error.message);
    }
  };

  const copyPassword = async (item) => {
    try {
      const password = revealedPasswords[item.id] || await revealPortalCredentialPassword(resolvedCredentialScope, item.id);
      await writeToClipboard(password);
      showCopyMessage("Şifre kopyalandı.");
    } catch (error) {
      alert("Şifre kopyalanamadı: " + getPortalErrorMessage(error));
    }
  };

  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();

    reader.onload = async (loadEvent) => {
      try {
        const workbook = XLSX.read(loadEvent.target.result, { type: "array" });
        const rows = workbook.SheetNames.flatMap((sheetName) =>
          getRowsFromWorksheet(workbook.Sheets[sheetName], sheetName)
        );

        if (!rows.length) {
          alert(
            "Dosyada uygun başlık satırı bulunamadı.\n\n" +
            "Beklenen alanlar: Teknopark Adı, Teknopark URL/Portal Linki, Kullanıcı Adı, Şifre, Açıklama"
          );
          return;
        }

        const invalidRows = rows
          .map((row) => {
            const missingFields = [
              !row.technoparkName ? "Teknopark" : "",
              !row.portalUrl ? "Portal Linki" : "",
              !row.username ? "Kullanıcı Adı" : "",
              !row.password ? "Şifre" : ""
            ].filter(Boolean);
            return missingFields.length ? `${row.sourceLabel}: ${missingFields.join(", ")}` : "";
          })
          .filter(Boolean);

        if (invalidRows.length) {
          alert(
            `${invalidRows.length} satırda zorunlu alan eksik. Dosya içe aktarılmadı.\n\n` +
            invalidRows.slice(0, 8).join("\n") +
            (invalidRows.length > 8 ? `\n... ${invalidRows.length - 8} satır daha` : "")
          );
          return;
        }

        if (rows.length > 500) {
          alert(
            `Dosyada ${rows.length} kayıt bulundu. Güvenli içe aktarma limiti şu an tek seferde 500 kayıt.\n\n` +
            "İstersen bir sonraki adımda bunu otomatik parçalı yükleme olarak düzenleyebilirim."
          );
          return;
        }

        if (!(await window.appConfirm(`${rows.length} portal kaydı içe aktarılsın mı?`, { title: "Portal kayıtları yüklensin mi?", type: "warning", confirmText: "Yükle" }))) return;
        const result = await importPortalCredentials(resolvedCredentialScope, rows.map(({ sourceLabel, ...row }) => row));
        const createdCount = result.createdCount ?? result.importedCount ?? 0;
        const updatedCount = result.updatedCount ?? 0;
        if (createdCount > 0 || updatedCount > 0) {
          await notifyTeam?.({
            title: "Portal bilgileri içe aktarıldı",
            body: `${actorName}, Excel/CSV ile ${createdCount} portal bilgisi ekledi, ${updatedCount} portal bilgisini güncelledi.`,
            type: "portal",
            link: "portalCredentials"
          });
        }
        await loadCredentials();
        alert(
          `${result.importedCount} portal kaydı işlendi.\n\n` +
          `Yeni eklenen: ${createdCount}\n` +
          `Güncellenen: ${updatedCount}` +
          ((result.duplicateInFileCount ?? 0) > 0
            ? `\nDosya içi tekrar: ${result.duplicateInFileCount}`
            : "")
        );
      } catch (error) {
        console.error(error);
        alert("Dosya içe aktarılamadı: " + getPortalErrorMessage(error));
      } finally {
        event.target.value = "";
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="force-light-form">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: "#124254" }}>Portal Bilgileri</h2>
          <div style={{ color: "#64748b", fontWeight: 700, marginTop: 6 }}>
            Teknopark portallarına ait şifrelenmiş erişim bilgileri
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button type="button" onClick={openNewCredentialModal} style={{ ...primaryButton, background: "#0f766e" }}>
            + Yeni Portal Bilgisi
          </button>
          <label style={{ padding: "10px 14px", borderRadius: 10, background: "#0f766e", color: "#fff", fontWeight: 900, cursor: "pointer" }}>
            Excel / CSV Yükle
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} style={{ display: "none" }} />
          </label>
        </div>
      </div>

      <div className="force-light-form" style={{ background: "#fff", padding: 20, borderRadius: 18, boxShadow: "0 8px 24px rgba(15,52,66,.07)" }}>
        <input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Teknopark, kullanıcı adı veya açıklama ara..." style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: 14 }} />
        {copyMessage && (
          <div style={copyMessageStyle}>
            {copyMessage}
          </div>
        )}
        {loading ? <div>Yükleniyor...</div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 1040, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[
                    ["Teknopark", "18%"],
                    ["Portal", "8%"],
                    ["Kullanıcı Adı", "24%"],
                    ["Şifre", "16%"],
                    ["Açıklama", "22%"],
                    ["İşlem", "12%"]
                  ].map(([title, width]) => (
                    <th key={title} style={{ width, textAlign: "left", padding: 11, borderBottom: "1px solid #e2e8f0" }}>{title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleCredentials.map((item) => (
                  <tr key={item.id}>
                    <td style={cellStyle}><strong>{item.technoparkName}</strong></td>
                    <td style={cellStyle}><a href={item.portalUrl} target="_blank" rel="noreferrer" style={{ fontWeight: 800, whiteSpace: "nowrap" }}>Portalı Aç</a></td>
                    <td style={cellStyle}>
                      <div style={inlineCellContentStyle}>
                        <span style={valueTextStyle}>{item.username}</span>
                        <button type="button" aria-label="Kullanıcı adını kopyala" title="Kullanıcı adını kopyala" onClick={() => copyUsername(item)} style={copyIconButton}>
                          ⧉
                        </button>
                      </div>
                    </td>
                    <td style={cellStyle}>
                      <div style={inlineCellContentStyle}>
                        <span style={{ ...valueTextStyle, fontFamily: "monospace", letterSpacing: revealedPasswords[item.id] ? 0 : 1 }}>{revealedPasswords[item.id] || "••••••••"}</span>
                        <button
                          type="button"
                          aria-label={revealedPasswords[item.id] ? "Şifreyi gizle" : "Şifreyi göster"}
                          title={revealedPasswords[item.id] ? "Şifreyi gizle" : "Şifreyi göster"}
                          onClick={() => revealPassword(item)}
                          style={copyIconButton}
                        >
                          {revealedPasswords[item.id] ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                        <button type="button" aria-label="Şifreyi kopyala" title="Şifreyi kopyala" onClick={() => copyPassword(item)} style={copyIconButton}>
                          ⧉
                        </button>
                      </div>
                    </td>
                    <td style={cellStyle}>{item.description || "-"}</td>
                    <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                      <button onClick={() => openEditCredentialModal(item)} style={{ ...secondaryButton, marginRight: 6 }}>Düzenle</button>
                      <button onClick={async () => {
                        if (!(await window.appConfirm(`${item.technoparkName} kaydı silinsin mi?`, { title: "Portal kaydı silinsin mi?", type: "danger", confirmText: "Sil" }))) return;
                        try {
                          await deletePortalCredential(resolvedCredentialScope, item.id);
                          await loadCredentials();
                        } catch (error) {
                          alert("Portal bilgisi silinemedi: " + getPortalErrorMessage(error));
                        }
                      }} style={{ ...secondaryButton, color: "#b91c1c" }}>Sil</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!visibleCredentials.length && <div style={{ color: "#64748b", padding: 14 }}>Portal kaydı bulunamadı.</div>}
          </div>
        )}
      </div>

      {isFormOpen && (
        <div
          onClick={closeCredentialModal}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(15,23,42,0.52)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20
          }}
        >
          <div
            className="force-light-form"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(760px, 100%)",
              background: "#fff",
              borderRadius: 20,
              padding: 22,
              boxShadow: "0 24px 80px rgba(15,23,42,0.35)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, color: "#124254", fontSize: 22 }}>
                  {form.id ? "Portal Bilgisini Güncelle" : "Yeni Portal Bilgisi"}
                </h3>
                <div style={{ color: "#64748b", fontWeight: 700, marginTop: 5, fontSize: 13 }}>
                  Teknopark portal erişim bilgilerini buradan kaydedebilirsiniz.
                </div>
              </div>

              <button type="button" onClick={closeCredentialModal} style={{ ...secondaryButton, padding: "8px 10px" }}>
                Kapat
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
              <select
                value={isCustomTechnopark ? CUSTOM_TECHNOPARK_VALUE : form.technoparkName}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  const isOther = selectedValue === CUSTOM_TECHNOPARK_VALUE;
                  setIsCustomTechnopark(isOther);
                  setForm({ ...form, technoparkName: isOther ? "" : selectedValue });
                }}
                style={inputStyle}
              >
                <option value="">Teknopark seçin</option>
                {TECHNOPARKS.map((technopark) => (
                  <option key={technopark} value={technopark}>{technopark}</option>
                ))}
                <option value={CUSTOM_TECHNOPARK_VALUE}>Diğer (listede yok)</option>
              </select>
              {isCustomTechnopark && (
                <input
                  autoFocus
                  value={form.technoparkName}
                  onChange={(e) => setForm({ ...form, technoparkName: e.target.value })}
                  placeholder="Teknopark adını yazın"
                  style={inputStyle}
                />
              )}
              <input value={form.portalUrl} onChange={(e) => setForm({ ...form, portalUrl: e.target.value })} placeholder="https://portal..." style={inputStyle} />
              <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="Kullanıcı adı" style={inputStyle} />
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={form.id ? "Değişmeyecekse boş bırakın" : "Şifre"} style={inputStyle} />
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Açıklama"
                style={{ ...inputStyle, gridColumn: "1 / -1", minHeight: 90, resize: "vertical" }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button type="button" onClick={closeCredentialModal} style={secondaryButton}>Vazgeç</button>
              <button disabled={saving} onClick={saveCredential} style={{ ...primaryButton, opacity: saving ? 0.7 : 1 }}>
                {saving ? "Kaydediliyor..." : form.id ? "Güncelle" : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = { padding: "11px 12px", borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 };
const primaryButton = { padding: "10px 15px", border: "none", borderRadius: 9, background: "#124254", color: "#fff", fontWeight: 900, cursor: "pointer" };
const secondaryButton = { padding: "9px 11px", border: "1px solid #cbd5e1", borderRadius: 9, background: "#fff", color: "#334155", fontWeight: 800, cursor: "pointer" };
const cellStyle = { padding: 11, borderBottom: "1px solid #f1f5f9", color: "#334155" };
const inlineCellContentStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "wrap"
};
const valueTextStyle = {
  display: "inline-block",
  maxWidth: "100%",
  overflowWrap: "anywhere"
};
const copyIconButton = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  background: "#f8fafc",
  color: "#334155",
  fontWeight: 900,
  cursor: "pointer",
  lineHeight: 1
};
const copyMessageStyle = {
  display: "inline-flex",
  alignItems: "center",
  marginBottom: 12,
  padding: "8px 12px",
  borderRadius: 999,
  background: "#dcfce7",
  color: "#166534",
  fontWeight: 900,
  fontSize: 13
};

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10.6 10.6a2 2 0 0 0 2.8 2.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M9.9 5.3A9.9 9.9 0 0 1 12 5c6 0 9.5 7 9.5 7a15.2 15.2 0 0 1-3 3.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.3 6.8C3.9 8.5 2.5 12 2.5 12s3.5 7 9.5 7c1.4 0 2.7-.4 3.8-1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default PortalCredentialsView;
